/**
 * AcceptBidDialog Component
 * 3-step dialog for accepting a bid: confirmation, payment, and success
 */

import { useState } from 'react';
import type { PlanBidWithPricing, ServiceProvider, AcceptBidResponse } from '../../types';
import { formatCurrency } from '../../utils/formatting';
import PaymentForm from './PaymentForm';

type DialogStep = 'confirm' | 'payment' | 'success';

interface AcceptBidDialogProps {
  bid: PlanBidWithPricing & { service_provider: ServiceProvider };
  isOpen: boolean;
  onAccept: () => Promise<AcceptBidResponse>;
  onClose: () => void;
  onPaymentSuccess: () => void;
}

export function AcceptBidDialog({ bid, isOpen, onAccept, onClose, onPaymentSuccess }: AcceptBidDialogProps) {
  const [currentStep, setCurrentStep] = useState<DialogStep>('confirm');
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<{
    clientSecret: string;
    depositAmount: number;
    bidId: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const pricing = bid.proposed_pricing;

  // Reset state when dialog closes
  const handleClose = () => {
    setCurrentStep('confirm');
    setLoading(false);
    setPaymentData(null);
    setError(null);
    onClose();
  };

  // Step 1: Handle confirmation and initiate payment
  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await onAccept();
      setPaymentData({
        clientSecret: response.payment_client_secret,
        depositAmount: response.deposit_amount_cents,
        bidId: response.bid_id
      });
      setCurrentStep('payment');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept bid';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Handle successful payment
  const handlePaymentSuccess = () => {
    setCurrentStep('success');
    onPaymentSuccess();
  };

  // Step 2: Handle payment error
  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Determine if backdrop should close dialog
  const handleBackdropClick = () => {
    if (currentStep === 'confirm' && !loading) {
      handleClose();
    } else if (currentStep === 'success') {
      handleClose();
    }
    // Payment step cannot be dismissed by clicking backdrop
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      ></div>

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Step 1: Confirmation */}
          {currentStep === 'confirm' && (
            <>
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-accent-100 flex items-center justify-center">
                    <svg
                      className="h-6 w-6 text-accent-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-neutral-900">
                      Accept This Bid?
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      From {bid.service_provider.name}
                    </p>
                  </div>
                </div>
                {!loading && (
                  <button
                    onClick={handleClose}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="mb-6">
                {/* Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-semibold text-yellow-800">Important</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Accepting this bid will automatically reject all other pending bids for this plan.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bid Summary */}
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-neutral-900 mb-3">Bid Summary</h4>

                  {/* Service Description */}
                  <div className="mb-3">
                    <p className="text-sm text-neutral-700 font-medium mb-1">Service Description:</p>
                    <p className="text-sm text-neutral-600">{bid.service_description}</p>
                  </div>

                  {/* Key Pricing */}
                  <div className="border-t border-neutral-200 pt-3">
                    <p className="text-sm text-neutral-700 font-medium mb-2">Key Pricing:</p>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      {pricing.hauling_rate_per_ton !== undefined && (
                        <>
                          <dt className="text-neutral-600">Hauling Rate:</dt>
                          <dd className="text-neutral-900 font-semibold text-right">
                            {formatCurrency(pricing.hauling_rate_per_ton)}/ton
                          </dd>
                        </>
                      )}
                      {pricing.bin_rental_20yd !== undefined && (
                        <>
                          <dt className="text-neutral-600">20yd Container:</dt>
                          <dd className="text-neutral-900 font-semibold text-right">
                            {formatCurrency(pricing.bin_rental_20yd)}
                          </dd>
                        </>
                      )}
                      {pricing.delivery_fee !== undefined && (
                        <>
                          <dt className="text-neutral-600">Delivery Fee:</dt>
                          <dd className="text-neutral-900 font-semibold text-right">
                            {formatCurrency(pricing.delivery_fee)}
                          </dd>
                        </>
                      )}
                      {pricing.pickup_fee !== undefined && (
                        <>
                          <dt className="text-neutral-600">Pickup Fee:</dt>
                          <dd className="text-neutral-900 font-semibold text-right">
                            {formatCurrency(pricing.pickup_fee)}
                          </dd>
                        </>
                      )}
                    </dl>
                  </div>

                  {/* Contact */}
                  <div className="border-t border-neutral-200 pt-3 mt-3">
                    <p className="text-sm text-neutral-700 font-medium mb-1">Provider Contact:</p>
                    <p className="text-sm text-neutral-600">
                      {bid.service_provider.contact_email} | {bid.service_provider.contact_phone}
                    </p>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-primary-900 mb-1">Next Steps</h4>
                  <p className="text-sm text-primary-800">
                    After accepting, you&apos;ll complete the deposit payment to create a job with this service provider.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading}
                  className="btn-primary bg-accent-600 hover:bg-accent-700"
                >
                  {loading ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      Accepting Bid...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 mr-2 inline"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Accept Bid
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {/* Step 2: Payment */}
          {currentStep === 'payment' && paymentData && (
            <>
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <svg
                      className="h-6 w-6 text-primary-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-neutral-900">
                      Complete Payment
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Deposit for {bid.service_provider.name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                {/* Deposit Amount Display */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-primary-700 font-medium">Deposit Amount</p>
                      <p className="text-xs text-primary-600 mt-0.5">
                        Required to secure this service provider
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-primary-900">
                      {formatCurrency(paymentData.depositAmount / 100)}
                    </p>
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Form */}
                <PaymentForm
                  clientSecret={paymentData.clientSecret}
                  depositAmount={paymentData.depositAmount}
                  bidId={paymentData.bidId}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              </div>
            </>
          )}

          {/* Step 3: Success */}
          {currentStep === 'success' && (
            <>
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-neutral-900">
                      Success!
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Payment completed
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="mb-6">
                {/* Success Message */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                      <svg
                        className="h-10 w-10 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-green-900 mb-2">
                    Payment Successful!
                  </h4>
                  <p className="text-sm text-green-800 mb-4">
                    Your deposit has been processed and the job has been created with {bid.service_provider.name}.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-neutral-700 mb-2">
                      <span className="font-semibold">What&apos;s next?</span>
                    </p>
                    <ul className="text-sm text-neutral-600 space-y-1 text-left">
                      <li className="flex items-start">
                        <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Track materials and manage your project in the Jobs section</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Coordinate with your service provider for service delivery</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Monitor invoices and remaining balance as work progresses</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleClose}
                  className="btn-primary bg-accent-600 hover:bg-accent-700"
                >
                  <svg
                    className="w-5 h-5 mr-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
